import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Box, IconButton } from '@mui/material';
import { LayoutGridIcon } from '../../../icons/animated';

const MotionIconButton = motion(IconButton);

export interface AppsDockTriggerProps {
  onClick: () => void;
  isOpen?: boolean;
}

export const AppsDockTrigger: React.FC<AppsDockTriggerProps> = ({
  onClick,
  isOpen = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutHint = isMac ? '⌘+Space' : 'Ctrl+Space';

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <MotionIconButton
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label="Applications Launcher"
        title={`Applications Launcher (${shortcutHint} or Ctrl+Space)`}
        sx={{
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 },
          borderRadius: 3,
          transition: 'all 0.2s ease',
          backdropFilter: 'blur(12px)',
          color: 'var(--navbar-text, #ffffff)',
          border: '1px solid',
          borderColor: isOpen
            ? 'var(--navbar-btn-border, rgba(255, 255, 255, 0.6))'
            : 'var(--navbar-btn-border, rgba(255, 255, 255, 0.2))',
          bgcolor: isOpen
            ? 'var(--navbar-btn-hover, rgba(255, 255, 255, 0.3))'
            : 'var(--navbar-btn-bg, rgba(255, 255, 255, 0.1))',
          boxShadow: isOpen
            ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
            : '0 1px 2px rgba(0, 0, 0, 0.05)',
          '&:hover': {
            bgcolor: 'var(--navbar-btn-hover, rgba(255, 255, 255, 0.2))',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <LayoutGridIcon
          size={20}
          isHovered={isHovered}
          color="var(--navbar-text, #ffffff)"
        />
      </MotionIconButton>

      {/* Subtle active status indicator */}
      {isOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#34d399',
            boxShadow: '0 0 0 2px #064e3b',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
};

export default AppsDockTrigger;
